import { useState } from 'react'
import { cn } from 'lib/utils'

export type SiteAvatarSize = 'sm' | 'md'

export type SiteAvatarProps = {
	/** Image URL to show when available. Falls back to the letter badge on missing/404. */
	imageUrl?: string | null
	/** Letter shown when no image is available (A, B, C, …). */
	letter: string
	/** Size variant. 'sm' = 32 px, 'md' = 40 px. Default 'md'. */
	size?: SiteAvatarSize
	className?: string
}

const SIZE_MAP: Record<SiteAvatarSize, { wrapper: string; imgSize: string; textSize: string }> = {
	sm: { wrapper: 'w-8 h-8', imgSize: 'w-8 h-8', textSize: 'text-xs' },
	md: { wrapper: 'w-10 h-10', imgSize: 'w-10 h-10', textSize: 'text-sm' },
}

/**
 * Per-site avatar: shows the linked Location's image when available, falls back
 * to a letter badge (e.g. "A", "B", "C"). Tracks its own image-load error state
 * so a bad URL degrades to the letter fallback without affecting siblings.
 */
export function SiteAvatar({ imageUrl, letter, size = 'md', className }: SiteAvatarProps) {
	const [errored, setErrored] = useState(false)
	const showImage = imageUrl && !errored
	const sizes = SIZE_MAP[size]

	return (
		<div className={cn(sizes.wrapper, 'rounded-lg overflow-hidden shrink-0', className)}>
			{showImage ? (
				<img
					src={imageUrl!}
					alt=""
					loading="lazy"
					onError={() => setErrored(true)}
					className={cn(sizes.imgSize, 'object-cover')}
				/>
			) : (
				<div
					className={cn(
						sizes.wrapper,
						'flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe] font-semibold',
						sizes.textSize,
					)}
				>
					{letter}
				</div>
			)}
		</div>
	)
}
